import { create } from 'zustand';
import type { PricingTier, ProductVariant } from '../types/pricing';

interface PricingState {
  // Pricing data
  pricingTiers: PricingTier[];
  basePrice: number;

  // Quantity
  quantity: number;
  minQuantity: number;
  maxQuantity: number;

  // Variants
  variants: ProductVariant[];
  selectedVariantId: string | number | null;

  // Calculated values
  unitPrice: number;
  totalPrice: number;
  currentTier: PricingTier | null;
  savingsPercent: number;

  // Loading
  isLoading: boolean;
  error: string | null;

  // Actions
  setPricingTiers: (tiers: PricingTier[], basePrice: number) => void;
  setQuantity: (quantity: number) => void;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  setVariants: (variants: ProductVariant[]) => void;
  selectVariant: (variantId: string | number) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  calculatePrice: () => void;
}

export const usePricingStore = create<PricingState>((set, get) => ({
  pricingTiers: [],
  basePrice: 0,

  quantity: 1,
  minQuantity: 1,
  maxQuantity: 9999,

  variants: [],
  selectedVariantId: null,

  unitPrice: 0,
  totalPrice: 0,
  currentTier: null,
  savingsPercent: 0,

  isLoading: false,
  error: null,

  setPricingTiers: (pricingTiers, basePrice) => {
    set({ pricingTiers, basePrice });
    get().calculatePrice();
  },

  setQuantity: (quantity) => {
    const { minQuantity, maxQuantity } = get();
    const clampedQuantity = Math.max(minQuantity, Math.min(maxQuantity, quantity));
    set({ quantity: clampedQuantity });
    get().calculatePrice();
  },

  incrementQuantity: () => {
    const { quantity, maxQuantity } = get();
    if (quantity < maxQuantity) {
      set({ quantity: quantity + 1 });
      get().calculatePrice();
    }
  },

  decrementQuantity: () => {
    const { quantity, minQuantity } = get();
    if (quantity > minQuantity) {
      set({ quantity: quantity - 1 });
      get().calculatePrice();
    }
  },

  setVariants: (variants) => {
    set({ variants });
  },

  selectVariant: (variantId) => {
    set({ selectedVariantId: variantId });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  calculatePrice: () => {
    const { pricingTiers, basePrice, quantity } = get();

    if (pricingTiers.length === 0 || basePrice === 0) {
      set({
        unitPrice: basePrice,
        totalPrice: basePrice * quantity,
        currentTier: null,
        savingsPercent: 0,
      });
      return;
    }

    // Find applicable tier
    const applicableTier = pricingTiers.find(
      (tier) =>
        quantity >= tier.quantity_min &&
        (tier.quantity_max === undefined || quantity <= tier.quantity_max)
    );

    if (!applicableTier) {
      // Use base price if no tier matches
      set({
        unitPrice: basePrice,
        totalPrice: basePrice * quantity,
        currentTier: null,
        savingsPercent: 0,
      });
      return;
    }

    let unitPrice: number;

    switch (applicableTier.price_type) {
      case 'fixed':
        // Fixed price per unit
        unitPrice = applicableTier.discount_value;
        break;
      case 'percent':
        // Percentage discount from base
        unitPrice = basePrice * (1 - applicableTier.discount_value / 100);
        break;
      case 'price':
        // Use calculated_price if available
        unitPrice = applicableTier.calculated_price ?? basePrice;
        break;
      default:
        unitPrice = basePrice;
    }

    const totalPrice = unitPrice * quantity;
    const savingsPercent =
      basePrice > 0 ? Math.round(((basePrice - unitPrice) / basePrice) * 100) : 0;

    set({
      unitPrice,
      totalPrice,
      currentTier: applicableTier,
      savingsPercent,
    });
  },
}));

// Selectors
export const selectPricingDisplay = (state: PricingState) => ({
  unitPrice: state.unitPrice,
  totalPrice: state.totalPrice,
  quantity: state.quantity,
  savingsPercent: state.savingsPercent,
  hasTiers: state.pricingTiers.length > 0,
});

export const selectVariants = (state: PricingState) => ({
  variants: state.variants,
  selectedVariantId: state.selectedVariantId,
});
